import { ChangeEvent, useEffect, useState } from 'react';

import Selector from '../../shared/Selector';
import { GroupsManagementModal } from './GroupsManagementModal';
import { Loading, ManagePlaysets, Section } from '../../shared';

import {
    ProfileGroups,
    useProfileGroupsFromGoogleSheet,
} from '../../hooks/accounts';
import { usePacks } from '../../hooks/packs';

import { Pack } from '../../types/packTypes';

import styles from './GroupsManagement.module.scss';

export const GroupsManagementPage = () => {
    const GroupToTitle = {
        [ProfileGroups.TocaBoca]: 'Toca Boca Employees',
        [ProfileGroups.Superfans]: 'Superfans',
        [ProfileGroups.KidsPanel]: 'Kids Panel',
        [ProfileGroups.Influencers]: 'Influencers',
        [ProfileGroups.SpinMaster]: 'Spin Master',
        [ProfileGroups.Originator]: 'Originator',
        [ProfileGroups.SagoMini]: 'Sago Mini',
        [ProfileGroups.MelissaAndDoug]: 'Melissa and Doug',
        [ProfileGroups.TocaBocaFamily]: 'Toca Boca Family',
        [ProfileGroups.Other]: 'Other',
    };

    const [packIdsToAdd, setPackIdsToAdd] = useState<string[]>([]);
    const [isModalShown, setIsModalShown] = useState<boolean>(false);
    const [selectedGroup, setSelectedGroup] = useState<ProfileGroups | string>(
        '',
    );
    const {
        packs: allPacks = [],
        fetch: fetchPacks,
        isLoading: isPacksLoading,
        error: packsError,
    } = usePacks();
    const {
        profiles: allProfiles,
        fetch: fetchGroup,
        isLoading: isGroupLoading,
        error: groupsError,
    } = useProfileGroupsFromGoogleSheet();

    useEffect(() => {
        fetchPacks();
    }, []);

    useEffect(() => {
        if (!selectedGroup) {
            return;
        }

        fetchGroup(selectedGroup as ProfileGroups);
    }, [selectedGroup]);

    const onGroupChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const group = e.target.value as ProfileGroups;

        setSelectedGroup(group);
    };

    const togglePack = (pack: Pack) => {
        const packs = new Set(packIdsToAdd);
        const packId = pack.packId;

        if (packs.has(packId)) {
            packs.delete(packId);
        } else {
            packs.add(packId);
        }

        setPackIdsToAdd(Array.from(packs));
    };

    if (isPacksLoading) {
        return <Loading text="Loading..." />;
    }

    if (packsError || groupsError) {
        return (
            <Section title="Groups management">
                {packsError ? <div>{packsError}</div> : null}
                {groupsError ? <div>{groupsError}</div> : null}
            </Section>
        );
    }

    return (
        <Section title="Groups management">
            <div className={styles.controlPanel}>
                <Selector
                    id="profiles-group"
                    name="profiles-group"
                    onChange={onGroupChange}
                    disabled={isGroupLoading}
                >
                    <option value="">-- Select the group to update --</option>
                    {Object.values(ProfileGroups).map((group) => (
                        <option key={group} value={group}>
                            {GroupToTitle[group]}
                        </option>
                    ))}
                </Selector>
                <button
                    type="button"
                    onClick={() => setIsModalShown(true)}
                    disabled={
                        isGroupLoading || !selectedGroup || !packIdsToAdd.length
                    }
                >
                    Update selected
                </button>
            </div>

            <ManagePlaysets
                items={packIdsToAdd}
                playsets={allPacks}
                togglePlayset={togglePack}
            />

            {isModalShown && (
                <GroupsManagementModal
                    title={`${
                        GroupToTitle[selectedGroup as ProfileGroups]
                    } Management`}
                    packIdsToAdd={packIdsToAdd}
                    allProfiles={allProfiles}
                    allPacks={allPacks}
                    onModalCloseClicked={() => setIsModalShown(false)}
                />
            )}
        </Section>
    );
};
